// =====================================================================================
// INVRT Radar — web console — intent matcher (GUIDED MODE)
//
// A deterministic, rule-based matcher. No model, no network, no hidden state — the same
// input always produces the same intent, and every rule is visible in this file.
//
// It exists because the chat panel is the console's front door and must be trustworthy on
// day one: a person typing "how many credits have I got" gets the balance, not a
// plausible-sounding guess. When the model-driven loop lands (see src/chat/loop.ts) this
// matcher stays as the fallback for the offline and degraded paths.
//
// Scoring, deliberately simple: each intent owns a list of phrases and words. A matched
// phrase scores 3 (multi-word phrases are far stronger evidence than a single token); a
// matched word scores 1. Highest total wins; a tie falls to the intent declared first,
// which is why the order below is ranked by how much a wrong answer would cost — the
// money-adjacent intents sit above the informational ones. Nothing above the threshold
// means `unknown`, and `unknown` shows the menu rather than picking something.
// =====================================================================================

export type ChatIntent =
  | "find_people"
  | "site_visitors"
  | "setup_site"
  | "check_balance"
  | "pricing"
  | "suppression"
  | "help"
  | "unknown";

interface IntentRule {
  intent: ChatIntent;
  phrases: string[];
  words: string[];
}

/** Ranked: an intent earlier in this list wins a tie. */
const RULES: IntentRule[] = [
  {
    intent: "find_people",
    phrases: [
      "find people",
      "find me",
      "find contacts",
      "find leads",
      "search for people",
      "look for people",
      "build a list",
      "build me a list",
      "who should i sell to",
      "reveal contact",
      "reveal contacts",
      "get contact details",
      "decision makers",
      "heads of",
      "job title",
    ],
    words: ["prospect", "prospecting", "leads", "lead", "contacts", "reveal", "titles"],
  },
  {
    intent: "site_visitors",
    phrases: [
      "who visited",
      "who is visiting",
      "who has visited",
      "visited my site",
      "visited our site",
      "on my website",
      "on our website",
      "site visitors",
      "website visitors",
      "identified visitors",
      "who looked at",
      "companies on my site",
    ],
    words: ["visitors", "visitor", "traffic", "anonymous"],
  },
  {
    intent: "setup_site",
    phrases: [
      "set up my site",
      "set up a site",
      "add a site",
      "add my site",
      "install the pixel",
      "install pixel",
      "get the pixel",
      "pixel snippet",
      "verify the pixel",
      "is my pixel working",
      "track my website",
    ],
    words: ["pixel", "snippet", "install", "provision", "tag"],
  },
  {
    intent: "check_balance",
    phrases: [
      "check my balance",
      "my balance",
      "how many credits",
      "credits left",
      "credit balance",
      "how much have i got",
      "what is my balance",
      "credits remaining",
    ],
    words: ["balance", "credits", "wallet"],
  },
  {
    intent: "pricing",
    phrases: [
      "how much does",
      "how much is",
      "what does it cost",
      "what do you charge",
      "price list",
      "your prices",
      "cost per",
      "rate card",
    ],
    words: ["price", "prices", "pricing", "cost", "costs", "charge"],
  },
  {
    intent: "suppression",
    phrases: [
      "already have",
      "already own",
      "do not charge me",
      "don't charge me",
      "existing customers",
      "known customers",
      "suppression list",
      "exclude these",
      "opt out",
      "opted out",
    ],
    words: ["suppress", "suppression", "exclude", "excluded", "opt-out"],
  },
  {
    intent: "help",
    phrases: [
      "what can you do",
      "what can radar do",
      "how does this work",
      "help me",
      "getting started",
      "where do i start",
    ],
    words: ["help", "hello", "hi", "hey", "start"],
  },
];

/** Below this, the matcher says `unknown` rather than guessing. */
const THRESHOLD = 1;

export interface IntentMatch {
  intent: ChatIntent;
  /** The winning score. Useful for tests and for showing the matcher's own confidence. */
  score: number;
}

/** Lower-cases, strips punctuation to spaces, and collapses runs of whitespace. */
export function normaliseInput(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/['’]/g, "'")
    .replace(/[^a-z0-9'\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function matchIntent(raw: string): IntentMatch {
  const text = normaliseInput(raw);
  if (text === "") return { intent: "unknown", score: 0 };

  const tokens = new Set(text.split(" "));
  let best: IntentMatch = { intent: "unknown", score: 0 };

  for (const rule of RULES) {
    let score = 0;
    for (const phrase of rule.phrases) {
      if (text.includes(normaliseInput(phrase))) score += 3;
    }
    for (const word of rule.words) {
      if (tokens.has(word)) score += 1;
    }
    // Strictly greater, so the first rule in the ranked list wins a tie.
    if (score > best.score) best = { intent: rule.intent, score };
  }

  return best.score >= THRESHOLD ? best : { intent: "unknown", score: best.score };
}

/** The chips shown when nothing matched, and on the empty thread. */
export const INTENT_SUGGESTIONS: Array<{ intent: ChatIntent; label: string }> = [
  { intent: "find_people", label: "Find people to contact" },
  { intent: "site_visitors", label: "Who visited my site" },
  { intent: "setup_site", label: "Set up my site" },
  { intent: "check_balance", label: "Check my balance" },
  { intent: "pricing", label: "What things cost" },
  { intent: "suppression", label: "People I already have" },
];
